import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useOptionalSession } from "@/auth/session";
import { accountJustMade } from "@/lib/consent";
import { trackAccountCreated } from "@/lib/conversions";
import { useQueueActions } from "@/lib/hooks/envoy";
import { useSaveActions } from "@/lib/hooks/saved";
import { landingFor, takeIntent } from "@/lib/gate";
import { handOverPasted } from "@/lib/tools/handoff";

function sessionStore(): Storage | undefined {
    try {
        return window.sessionStorage;
    } catch {
        return undefined;
    }
}

/**
 * Back from the sign-in: the page the person was on, with what they
 * pressed done. Save saves the posting, Tailor queues it for a resume,
 * Get new matches opens the envoy seeded from the search (still off), and
 * Add my resume opens the upload, and the free tool's Tailor hands its
 * job back to the tool. Once a sign-in; a stale intent is dropped.
 */
export default function IntentOnReturn() {
    const navigate = useNavigate();
    const { save } = useSaveActions();
    const { add } = useQueueActions();
    const createdAt = useOptionalSession()?.createdAt;
    const done = useRef(false);

    useEffect(() => {
        if (done.current) return;
        done.current = true;
        const intent = takeIntent(sessionStore());
        if (!intent) return;
        if (intent.kind === "tool_tailor") {
            if (accountJustMade(createdAt)) trackAccountCreated(intent.path);
            if (intent.pasted) handOverPasted(intent.pasted);
        }
        navigate(landingFor(intent), { replace: true });
        if (intent.kind === "save" && intent.postingId)
            save.mutate({ postingId: intent.postingId });
        if (intent.kind === "tailor" && intent.postingId) {
            add.mutate({ postingId: intent.postingId });
        }
    }, [navigate, save, add, createdAt]);

    return null;
}
